<template>
  <Loading v-if="!isLoaded && !preventLoadingIndicator" />
  <ForwardSlots v-else-if="isLoaded">
    <component
      :is="VCalendar"
      v-bind="$attrs"
      ref="vcalendar"
      :locale="getLocaleLanguage"
      :min-date="minDate"
      :max-date="maxDate"
      :masks="masks"
      @dayclick="handleDayClick"
      @did-move="handleSwipeMove"
    />
  </ForwardSlots>
</template>

<script>
import { mapState } from 'pinia'
import { DATEPICKER_DATE_FORMAT } from '@packages/date/formats'

/**
 * Custom implementation of v-calendar datepicker
 * https://vcalendar.io/calendar
 */
export default {
  setup () {
    const VCalendar = shallowRef(null)
    const vcalendar = shallowRef(null)

    return {
      VCalendar,
      vcalendar,
    }
  },

  inheritAttrs: false,

  props: {
    minDate: {
      type: Date,
      default: new Date(),
    },

    maxDate: {
      type: Date,
      default: null,
    },

    masks: {
      type: Object,
      default: () => ({
        weekdays: 'WW',
      }),
    },

    dateFormat: {
      type: String,
      default: DATEPICKER_DATE_FORMAT,
    },

    preventLoadingIndicator: {
      type: Boolean,
      default: false,
    },
  },

  expose: ['vcalendar'],
  emits: ['dayclick', 'did-move', 'loaded'],

  data () {
    return {
      isLoaded: false,
    }
  },

  computed: {
    ...mapState(useLocaleStore, ['getLocaleLanguage']),
  },

  created () {
    if (process.browser) {
      this.loadCalendar()
    }
  },

  methods: {
    async loadCalendar () {
      const [{ Calendar }] = await Promise.all([
        import('v-calendar'),
        import('v-calendar/style.css'),
      ])

      this.VCalendar = Calendar
      this.isLoaded = true

      this.$emit('loaded')
    },

    handleSwipeMove (event) {
      this.$emit('did-move', event[0])
    },

    handleDayClick (event) {
      this.$emit('dayclick', {
        date: this.$dayjs(event.date).format(this.dateFormat),
        dateRaw: event.date,
      })
    },
  },
}
</script>

<style lang="scss">
@import '@layers/web/assets/scss/modules/_calendar';
</style>